import React from 'react'
import PropTypes from 'prop-types'
import { Grid } from 'react-virtualized'
import isHotKey from 'is-hotkey'
import classNames from 'classnames'

import { buildEmojiSearch, buildEmojiList } from '../utils'

import EmojiViewer from '../Viewer'
import EmojiPickerPreview from './Preview'
import * as Icons from 'components/Utils/Icons'

const POPOVER_WIDTH = 268
const POPOVER_TOP_HEIGHT = 40
const EMOJI_SIZE = 36
const POPOVER_INNER_HEIGHT = EMOJI_SIZE * 6

const emojiSearch = buildEmojiSearch()

class EmojiPicker extends React.PureComponent {
  static propTypes = {
    onClick: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
  }

  state = {
    index: 0,
    search: emojiSearch,
    items: buildEmojiList('', emojiSearch),
    itemsPerRow: 7,
    searchWord: '',
  }

  renderCell = ({ columnIndex, key, rowIndex, style }) => {
    const index = columnIndex + rowIndex * this.state.itemsPerRow
    const item = this.state.items[index]

    if (!item) {
      return false
    }

    const emoji = this.state.items[index]
    return (
      <div
        key={key}
        className={classNames(
          'flexbox flexbox-centered',
          'pointer radius-normal',
          'background-shade-45-on-hover',
          {
            'background-shade-45': index === this.state.index,
          }
        )}
        onMouseMove={e => this.setState({ index })}
        onClick={e => this.onClickEmoji(e, emoji, index)}
        style={style}
      >
        <EmojiViewer index={index} emoji={emoji} size={EMOJI_SIZE - 12} />
      </div>
    )
  }

  onKeyDown = event => {
    const { index, itemsPerRow, items } = this.state
    if (isHotKey('up', event)) {
      const updatedIndex = index - itemsPerRow
      if (updatedIndex >= 0) {
        this.setState({ index: updatedIndex })
      }
    }
    if (isHotKey('down', event)) {
      const updatedIndex = index + itemsPerRow
      if (updatedIndex <= items.length) {
        this.setState({ index: updatedIndex })
      }
    }
    if (isHotKey('left', event)) {
      const updatedIndex = index - 1
      if (updatedIndex >= 0) {
        this.setState({ index: updatedIndex })
      }
    }
    if (isHotKey('right', event)) {
      const updatedIndex = index + 1
      if (updatedIndex <= items.length) {
        this.setState({ index: updatedIndex })
      }
    }
    if (isHotKey('enter', event)) {
      const selectedEmoji = items[index]
      this.onClickEmoji(event, selectedEmoji, index)
    }
    if (isHotKey('escape', event)) {
      event.stopPropagation()
      event.preventDefault()
      this.props.onClose()
      return true
    }
  }

  onClickEmoji = (event, emoji, index) => {
    event.preventDefault()
    this.props.onClick(emoji, index)
    this.props.onClose()
    return true
  }

  onChangeSearchWord = event => {
    const searchWord = event.target.value.toLowerCase()
    this.setState({
      searchWord,
      index: 0,
      items: buildEmojiList(searchWord, emojiSearch),
    })
  }

  render() {
    const { index, items, itemsPerRow } = this.state
    const selectedEmoji = items[index]

    return (
      <div
        className="flexbox flexbox-column height-full no-selection"
        style={{ width: POPOVER_WIDTH }}
      >
        <div
          className="width-full flexbox flexbox-align-center padding-horizontal-normal border-bottom-small border-shade-50"
          style={{ height: POPOVER_TOP_HEIGHT }}
        >
          <Icons.SearchIcon
            size={16}
            className="shade-30 margin-right-normal"
          />
          <input
            type="text"
            value={this.state.searchWord}
            placeholder="Find the perfect emoji"
            autoFocus
            className={classNames(
              'border-none placeholder-shade-30 font-size-large height-full flexbox-grow background-transparent shade-05'
            )}
            onChange={this.onChangeSearchWord}
            onKeyDown={this.onKeyDown}
          />
        </div>
        <div>
          <Grid
            key={this.state.searchWord}
            className={'padding-small'}
            cellRenderer={this.renderCell}
            columnCount={Math.min(items.length, itemsPerRow)}
            columnWidth={EMOJI_SIZE}
            rowCount={Math.ceil(items.length / itemsPerRow)}
            rowHeight={EMOJI_SIZE}
            height={POPOVER_INNER_HEIGHT}
            width={POPOVER_WIDTH}
            overscanRowCount={5}
            scrollToRow={Math.floor(index / itemsPerRow)}
            scrollToColumn={index % itemsPerRow}
            onScroll={this.onScroll}
            tabIndex={null}
          />
        </div>

        <EmojiPickerPreview
          index={index}
          selectedEmoji={selectedEmoji}
          onClickEmoji={e => this.onClickEmoji(e, selectedEmoji, index)}
        />
      </div>
    )
  }
}

export default EmojiPicker
